import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  ChevronDown, 
  ChevronUp,
  Box,
  Filter,
  Gauge,
  Activity,
  Thermometer,
  Timer,
  Waves,
  Zap,
  Beaker,
  Droplets,
  RotateCw,
  Box as BoxIcon
} from 'lucide-react';
import { ProcessFlowDiagram } from './ProcessFlowDiagram';
import { AssetGauge } from './AssetGauge';
import { Asset } from '../types';

export const ROProductStage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    const initialAssets: Asset[] = [
      {
        id: 'product-analyzer',
        name: 'Product Analyzer',
        type: 'Quality Monitor',
        description: 'Final water quality monitoring system',
        manufacturer: 'Endress+Hauser',
        model: 'Liquiline CM444',
        installationDate: '2023-10-01',
        maintenanceSchedule: 'Weekly',
        status: 'operational',
        metrics: [
          {
            name: 'Conductivity',
            value: 5.2,
            unit: 'µS/cm',
            normalRange: { min: 0, max: 10 },
            criticalRange: { min: 0, max: 15 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 5.2 + Math.random() * 1 - 0.5
            }))
          },
          {
            name: 'pH',
            value: 7.0,
            unit: 'pH',
            normalRange: { min: 6.8, max: 7.2 },
            criticalRange: { min: 6.5, max: 7.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 7.0 + Math.random() * 0.1 - 0.05
            }))
          },
          {
            name: 'Turbidity',
            value: 0.12,
            unit: 'NTU',
            normalRange: { min: 0, max: 0.2 },
            criticalRange: { min: 0, max: 0.3 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.12 + Math.random() * 0.02 - 0.01
            }))
          }
        ]
      },
      {
        id: 'remineralization',
        name: 'Remineralization',
        type: 'Chemical System',
        description: 'Mineral dosing system',
        manufacturer: 'ProMinent',
        model: 'Sigma X Control',
        installationDate: '2023-10-05',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'Calcium Dosing',
            value: 15,
            unit: 'mg/L',
            normalRange: { min: 12, max: 18 },
            criticalRange: { min: 10, max: 20 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 15 + Math.random() * 1.2 - 0.6
            }))
          },
          {
            name: 'Magnesium Dosing',
            value: 4.5,
            unit: 'mg/L',
            normalRange: { min: 3, max: 6 },
            criticalRange: { min: 2, max: 7 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 4.5 + Math.random() * 0.6 - 0.3
            }))
          },
          {
            name: 'Solution Level',
            value: 78,
            unit: '%',
            normalRange: { min: 20, max: 90 },
            criticalRange: { min: 10, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 78 + Math.random() * 2 - 1
            }))
          }
        ]
      },
      {
        id: 'product-pump',
        name: 'Product Transfer Pump',
        type: 'Centrifugal Pump',
        description: 'Final product water transfer pump',
        manufacturer: 'Grundfos',
        model: 'CR 95-2',
        installationDate: '2023-10-10',
        maintenanceSchedule: 'Quarterly',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 85,
            unit: 'm³/h',
            normalRange: { min: 70, max: 100 },
            criticalRange: { min: 60, max: 110 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 85 + Math.random() * 6 - 3
            }))
          },
          {
            name: 'Pressure',
            value: 3.2,
            unit: 'bar',
            normalRange: { min: 2.8, max: 3.5 },
            criticalRange: { min: 2.5, max: 4.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 3.2 + Math.random() * 0.14 - 0.07
            }))
          },
          {
            name: 'Power Draw',
            value: 22,
            unit: 'kW',
            normalRange: { min: 18, max: 25 },
            criticalRange: { min: 15, max: 28 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 22 + Math.random() * 1.4 - 0.7
            }))
          }
        ]
      },
      {
        id: 'storage-tank',
        name: 'Product Storage Tank',
        type: 'Storage System',
        description: 'Final product water storage tank',
        manufacturer: 'Custom Built',
        model: 'ST-1000',
        installationDate: '2023-10-15',
        maintenanceSchedule: 'Annual',
        status: 'operational',
        metrics: [
          {
            name: 'Level',
            value: 82,
            unit: '%',
            normalRange: { min: 40, max: 90 },
            criticalRange: { min: 20, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 82 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Temperature',
            value: 21,
            unit: '°C',
            normalRange: { min: 15, max: 25 },
            criticalRange: { min: 10, max: 30 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 21 + Math.random() * 1 - 0.5
            }))
          },
          {
            name: 'Volume',
            value: 820,
            unit: 'm³',
            normalRange: { min: 400, max: 900 },
            criticalRange: { min: 200, max: 950 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 820 + Math.random() * 40 - 20
            }))
          }
        ]
      },
      {
        id: 'distribution-pump',
        name: 'Distribution Pump',
        type: 'Centrifugal Pump',
        description: 'Distribution system feed pump',
        manufacturer: 'Grundfos',
        model: 'CR 120-2',
        installationDate: '2023-10-20',
        maintenanceSchedule: 'Quarterly',
        status: 'warning',
        metrics: [
          {
            name: 'Flow Rate',
            value: 110,
            unit: 'm³/h',
            normalRange: { min: 90, max: 130 },
            criticalRange: { min: 80, max: 140 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 110 + Math.random() * 8 - 4
            }))
          },
          {
            name: 'Pressure',
            value: 4.8,
            unit: 'bar',
            normalRange: { min: 4.5, max: 5.2 },
            criticalRange: { min: 4.0, max: 5.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 4.8 + Math.random() * 0.14 - 0.07
            }))
          },
          {
            name: 'Efficiency',
            value: 82,
            unit: '%',
            normalRange: { min: 75, max: 85 },
            criticalRange: { min: 70, max: 90 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 82 + Math.random() * 2 - 1
            }))
          }
        ]
      }
    ];

    setAssets(initialAssets);

    const interval = setInterval(() => {
      setAssets(prevAssets => prevAssets.map(asset => ({
        ...asset,
        metrics: asset.metrics.map(metric => {
          const newValue = metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02);
          return {
            ...metric,
            value: newValue,
            history: [
              ...metric.history.slice(1),
              {
                timestamp: Date.now(),
                value: newValue
              }
            ]
          };
        })
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getAssetIcon = (assetId: string) => {
    if (assetId.includes('analyzer')) return <Activity className="h-6 w-6" />;
    if (assetId.includes('remineralization')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('pump')) return <RotateCw className="h-6 w-6" />;
    if (assetId.includes('tank')) return <BoxIcon className="h-6 w-6" />;
    return <Activity className="h-6 w-6" />;
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Temperature')) return <Thermometer className="h-4 w-4" />;
    if (metricName.includes('Flow')) return <Waves className="h-4 w-4" />;
    if (metricName.includes('Power')) return <Zap className="h-4 w-4" />;
    if (metricName.includes('Level') || metricName.includes('Volume')) return <BoxIcon className="h-4 w-4" />;
    if (metricName.includes('Pressure')) return <Gauge className="h-4 w-4" />;
    if (metricName.includes('pH') || metricName.includes('Conductivity') || metricName.includes('Dosing')) return <Beaker className="h-4 w-4" />;
    if (metricName.includes('Efficiency')) return <Activity className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  const isMetricInRange = (value: number, metric: Asset['metrics'][0]) => {
    if (!metric.normalRange) return true;
    return value >= metric.normalRange.min && value <= metric.normalRange.max;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-emerald-600 text-white flex items-center justify-between hover:bg-emerald-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <BoxIcon className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Stage 6 - RO Product</h2>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="mb-6">
            <ProcessFlowDiagram assets={assets} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            {assets.map(asset => 
              asset.metrics.slice(0, 1).map(metric => (
                <div key={`${asset.id}-${metric.name}`} className="bg-white rounded-lg shadow p-4">
                  <AssetGauge
                    value={metric.value}
                    min={metric.normalRange?.min || 0}
                    max={metric.normalRange?.max || 100}
                    unit={metric.unit}
                    title={`${asset.name} - ${metric.name}`}
                  />
                </div>
              ))
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <div
                key={asset.id}
                onClick={() => setSelectedAsset(asset.id === selectedAsset ? null : asset.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  asset.id === selectedAsset
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getAssetIcon(asset.id)}
                    <div>
                      <h3 className="font-medium">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {asset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between items-baseline">
                      <div className="flex items-center space-x-2">
                        {getMetricIcon(metric.name)}
                        <span className="text-sm text-gray-500">{metric.name}</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        isMetricInRange(metric.value, metric) 
                          ? 'text-gray-900' 
                          : 'text-red-600'
                      }`}>
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};